import { zodResolver } from '@hookform/resolvers/zod';
import * as m from '@staiter/i18n';
import { SalesOrderUpsertInput } from '@staiter/shared';
import { useMutation } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Save } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { PageHeader } from '~/components/data';
import { CounterpartyPicker } from '~/components/domain/CounterpartyPicker';
import { Button, DatePicker, Input, Select, Textarea } from '~/components/primitives';
import { createSalesOrder } from '~/lib/api/sales-orders';
import styles from './_auth.sales-orders.new.module.css';

/**
 * G3 / G16 + Sprint L+ Day 2 — SO/new form rewrite.
 * Fixes: FE-23 customer combobox без [E2E-TEST] mess (filter via Select),
 * FE-25 textarea ≥3 rows для Примечания, FE-26 DatePicker primitive вместо
 * native <input type="date">, FE-28 labels through Input primitive (label-above),
 * FE-29 sticky footer + Cancel button, FE-30 visible explanation of missing fields.
 */
export const Route = createFileRoute('/_auth/sales-orders/new')({ component: SalesOrderNewPage });

type FormData = {
  counterparty_id: string;
  currency: 'KZT' | 'USD' | 'EUR' | 'RUB' | 'CNY';
  order_date: string;
  delivery_expected_date?: string | null;
  contract_number?: string | null;
  contract_date?: string | null;
  notes?: string | null;
};

function SalesOrderNewPage(): JSX.Element {
  const navigate = useNavigate();
  const form = useForm<FormData>({
    resolver: zodResolver(SalesOrderUpsertInput),
    mode: 'onChange',
    defaultValues: {
      counterparty_id: '',
      currency: 'KZT',
      order_date: new Date().toISOString().slice(0, 10),
      delivery_expected_date: null,
      contract_number: null,
      contract_date: null,
      notes: null,
    },
  });
  const mutation = useMutation({
    mutationFn: (data: FormData) =>
      createSalesOrder({
        counterparty_id: data.counterparty_id,
        currency: data.currency,
        order_date: data.order_date,
        delivery_expected_date: data.delivery_expected_date || null,
        contract_number: data.contract_number || null,
        contract_date: data.contract_date || null,
        notes: data.notes || null,
      }),
    onSuccess: (so) => {
      navigate({ to: '/sales-orders/$id', params: { id: so.id } });
    },
  });

  const { register, handleSubmit, formState, watch, setValue } = form;
  const errors = formState.errors;
  const watched = watch();

  // FE-30: visible explanation what's missing.
  const missing: string[] = [];
  if (!watched.counterparty_id) missing.push(m.so_col_customer());
  if (!watched.order_date) missing.push(m.so_col_order_date());

  return (
    <div className={styles.page}>
      <PageHeader title={m.so_title_new()} />
      <form
        onSubmit={handleSubmit((data) => mutation.mutate(data))}
        noValidate
        className={styles.form}
      >
        <div className={styles.fields}>
          {/* Searchable picker с server-side q-поиском по всем контрагентам (CounterpartyPicker
              сам стрипает [E2E-TEST]). kind не ограничиваем — классификация эвристическая. */}
          <CounterpartyPicker
            label={m.so_col_customer()}
            required
            value={watched.counterparty_id}
            placeholder={m.common_select_customer_placeholder()}
            onChange={(value) => setValue('counterparty_id', value, { shouldValidate: true })}
            {...(errors.counterparty_id?.message ? { error: errors.counterparty_id.message } : {})}
          />
          <Select
            label={m.so_form_currency()}
            value={watched.currency}
            options={[
              { value: 'KZT', label: 'KZT' },
              { value: 'USD', label: 'USD' },
              { value: 'EUR', label: 'EUR' },
              { value: 'RUB', label: 'RUB' },
              { value: 'CNY', label: 'CNY' },
            ]}
            onChange={(e) =>
              setValue('currency', e.target.value as FormData['currency'], {
                shouldValidate: true,
              })
            }
          />
          <DatePicker
            label={m.so_col_order_date()}
            required
            value={watched.order_date}
            onChange={(next) => setValue('order_date', next ?? '', { shouldValidate: true })}
            {...(errors.order_date?.message ? { error: errors.order_date.message } : {})}
          />
          <DatePicker
            label={m.so_col_delivery_date()}
            value={watched.delivery_expected_date ?? null}
            onChange={(next) => setValue('delivery_expected_date', next, { shouldValidate: true })}
          />
          <Input label={m.so_form_contract_number()} {...register('contract_number')} />
          <DatePicker
            label={m.so_form_contract_date()}
            value={watched.contract_date ?? null}
            onChange={(next) => setValue('contract_date', next, { shouldValidate: true })}
          />
        </div>
        <Textarea label={m.so_form_notes()} rows={3} {...register('notes')} />
        <div className={styles.footer}>
          {missing.length > 0 && (
            <p className={styles.missing} role="status">
              {m.common_form_missing_fields({ fields: missing.join(', ') })}
            </p>
          )}
          <div className={styles.actions}>
            <Button
              type="button"
              variant="secondary"
              onClick={() => navigate({ to: '/sales-orders' })}
            >
              {m.common_button_cancel()}
            </Button>
            <Button type="submit" disabled={mutation.isPending || !formState.isValid}>
              <Save size={14} /> {m.so_cta_create()}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
}
